/**
 * 広告枠 (Web)。
 *
 * 収益モデルは広告表示で、ネイティブアプリでは Google AdMob、
 * Web では同じ Google のネットワークである AdSense を利用する。
 *
 * 「どこに出すか」の判断は @spli/app-logic の shouldShowAd に集約されており、
 * このコンポーネントは描画だけを担当する。
 * 広告 ID が未設定の開発環境では、レイアウト確認用のプレースホルダーを表示する。
 *
 * 配信スクリプトは index.html の <head> で読み込む (vite.config.ts が差し込む)。
 * AdSense はそのスクリプトでサイトの確認も行うため、広告枠のある画面だけで
 * 読み込む形にはできない。
 */
import { adSlotSize, shouldShowAd, useAdsConfig, type AdPlacement } from "@spli/app-logic";
import { useEffect, useRef } from "react";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

export interface AdSlotProps {
  placement: AdPlacement;
  expenseCount: number;
  justSettled?: boolean;
}

export function AdSlot({ placement, expenseCount, justSettled }: AdSlotProps) {
  const config = useAdsConfig();
  const insRef = useRef<HTMLModElement>(null);
  /** この枠を AdSense へ渡したか (二重に渡すと TagError になる) */
  const filled = useRef(false);
  const unit = config.units[placement];
  const visible = shouldShowAd(config, { placement, expenseCount, justSettled });

  useEffect(() => {
    if (!visible || !unit || !config.clientId) return;
    /*
     * 配信スクリプト (adsbygoogle.js) は index.html の <head> で読み込んでいる。
     * ここでやるのは「この ins に広告を入れて」と伝えることだけ。
     *
     * push は ins 1 つにつき 1 回だけにする。React の再描画や
     * StrictMode の二重実行でもう一度呼ぶと、AdSense は
     * 「すでに広告が入っている」という TagError を投げ、以降の枠も出なくなる。
     */
    if (filled.current) return;
    filled.current = true;
    try {
      window.adsbygoogle = window.adsbygoogle ?? [];
      window.adsbygoogle.push({});
    } catch {
      // 広告の失敗でアプリを壊さない
    }
  }, [config.clientId, unit, visible]);

  if (!visible || !unit || !config.clientId) {
    /*
     * 広告を出さない場面。
     *
     * 開発中 (testMode) だけ、レイアウト確認用の枠を出す。
     * 本番では何も描画しない — 中身の入らない枠や「広告枠」という文字が
     * 利用者に見えるだけで、場所を空けておく意味が無いため。
     */
    if (!config.testMode) return null;

    const size = adSlotSize(unit?.format ?? "banner");
    return (
      <div
        className="ad-slot"
        style={{ width: "100%", maxWidth: size.width, height: size.height }}
        aria-hidden="true"
      >
        広告枠 ({placement})
      </div>
    );
  }

  const size = adSlotSize(unit.format);
  return (
    <ins
      ref={insRef}
      className="adsbygoogle ad-slot"
      style={{ display: "block", width: "100%", maxWidth: size.width, height: size.height }}
      data-ad-client={config.clientId}
      data-ad-slot={unit.unitId}
      data-adtest={config.testMode ? "on" : "off"}
    />
  );
}
